// src/pages/Tickets/TicketLinksPanel.jsx
// -----------------------------------------------------------------------------
// "Links" tab on Ticket Detail (Additional Requirement: ticket linking -
// Duplicate / Related / Parent-Child). Only tickets of the same project can be
// linked; the API enforces this too. Link types are configured by the Admin.
// -----------------------------------------------------------------------------
import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import ticketService from '../../api/ticketService';
import ticketLinkTypeService from '../../api/ticketLinkTypeService';

export default function TicketLinksPanel({ ticket, canEdit }) {
  const [links, setLinks] = useState([]);
  const [linkTypes, setLinkTypes] = useState([]);
  const [projectTickets, setProjectTickets] = useState([]);
  const [linkedTicketID, setLinkedTicketID] = useState('');
  const [linkTypeID, setLinkTypeID] = useState('');
  const [saving, setSaving] = useState(false);

  const loadLinks = () =>
    ticketService
      .getLinks(ticket.ticketID)
      .then((res) => setLinks(res.data || []))
      .catch(() => setLinks([]));

  useEffect(() => {
    loadLinks();
    ticketLinkTypeService.getAll().then((res) => setLinkTypes(res.data || [])).catch(() => {});
    ticketService
      .getAll({ projectID: ticket.projectID })
      .then((res) =>
        setProjectTickets(
          (res.data || []).filter(
            (t) => Number(t.projectID) === Number(ticket.projectID) && Number(t.ticketID) !== Number(ticket.ticketID)
          )
        )
      )
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticket.ticketID]);

  const ticketLabel = (id) => {
    const t = projectTickets.find((x) => Number(x.ticketID) === Number(id));
    return t ? `${t.ticketNumber} — ${t.title}` : `Ticket #${id}`;
  };
  const typeName = (id) => linkTypes.find((lt) => Number(lt.linkTypeID) === Number(id))?.linkTypeName || 'Linked';

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!linkedTicketID || !linkTypeID) {
      toast.warning('Select a ticket and a link type.');
      return;
    }
    setSaving(true);
    try {
      await ticketService.addLink(ticket.ticketID, {
        linkedTicketID: Number(linkedTicketID),
        linkTypeID: Number(linkTypeID),
      });
      setLinkedTicketID('');
      setLinkTypeID('');
      await loadLinks();
      toast.success('Ticket linked.');
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.response?.data || 'Failed to link ticket.');
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async (linkID) => {
    if (!window.confirm('Remove this link?')) return;
    try {
      await ticketService.removeLink(linkID);
      await loadLinks();
      toast.success('Link removed.');
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to remove link.');
    }
  };

  return (
    <div>
      {canEdit && (
        <form onSubmit={handleAdd} style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
          <select value={linkTypeID} onChange={(e) => setLinkTypeID(e.target.value)} style={{ minWidth: 160 }}>
            <option value="">-- Link type --</option>
            {linkTypes.map((lt) => (
              <option key={lt.linkTypeID} value={lt.linkTypeID}>
                {lt.linkTypeName}
              </option>
            ))}
          </select>
          <select value={linkedTicketID} onChange={(e) => setLinkedTicketID(e.target.value)} style={{ flex: 1, minWidth: 220 }}>
            <option value="">-- Ticket in this project --</option>
            {projectTickets.map((t) => (
              <option key={t.ticketID} value={t.ticketID}>
                {t.ticketNumber} — {t.title}
              </option>
            ))}
          </select>
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? 'Linking...' : 'Add Link'}
          </button>
        </form>
      )}

      {links.length === 0 ? (
        <p className="text-muted">No linked tickets.</p>
      ) : (
        links.map((l) => {
          // Show the link from this ticket's point of view
          const otherID = Number(l.ticketID) === Number(ticket.ticketID) ? l.linkedTicketID : l.ticketID;
          return (
            <div key={l.linkID} className="comment-item" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <span className="badge badge-info" style={{ marginRight: 8 }}>{typeName(l.linkTypeID)}</span>
                <Link to={`/tickets/${otherID}`}>{ticketLabel(otherID)}</Link>
              </div>
              {canEdit && (
                <button className="btn-table-action btn-table-action-delete" onClick={() => handleRemove(l.linkID)}>
                  Remove
                </button>
              )}
            </div>
          );
        })
      )}
    </div>
  );
}

TicketLinksPanel.propTypes = {
  ticket: PropTypes.shape({
    ticketID: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    projectID: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }).isRequired,
  canEdit: PropTypes.bool,
};
